import { Auth } from 'aws-amplify';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { UserDTO, IconWithTextRecord } from '../types';
import { UsernameExistsException } from '../util/Exceptions';

type AWSError = {
  code?: string;
  message?: string;
}

// Helper function to check for more than three repetitive characters
const hasMoreThanThreeRepetitiveCharacters = (password: string): boolean => {
  for (let i = 0; i < password.length - 3; i++) {
    if (password[i] === password[i + 1] && password[i] === password[i + 2] && password[i] === password[i + 3]) {
      return true;
    }
  }
  return false;
};

// Helper function to check for more than three sequential characters
const hasMoreThanThreeSequentialCharacters = (password: string): boolean => {
  const sequentialStrings = [
    '0123456789',
    'abcdefghijklmnopqrstuvwxyz',
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  ];

  for (let i = 0; i < password.length - 3; i++) {
    for (const seq of sequentialStrings) {
      if (seq.includes(password.substring(i, i + 4))) {
        return true;
      }
    }
  }
  return false;
};

// Helper function to check if string is valid email
const isEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export default class CognitoAPI {

  static async signIn(email_address: string, password: string): Promise<UserDTO> {
    try {
      const cognitoUser: CognitoUser = await Auth.signIn(email_address, password);
      // Get the user_guid from the session
      const session = cognitoUser.getSignInUserSession();
      if (session) {
        const idTokenPayload = session.getIdToken().decodePayload();
        const user_guid = idTokenPayload["sub"];
        const userDTO: UserDTO = {
          id: 0,
          user_guid: user_guid,
          preferredContactMethodId: 0,
          cellphone: '',
          timeZone: ''
        }
        return userDTO;
      } else {
        throw new Error('No session found');
      }
    } catch (error) {
      if ((error as AWSError).code === 'UserNotFoundException' || (error as AWSError).code === 'NotAuthorizedException') {
        console.log('Error signing in', error);
        throw new Error('The email address or password is incorrect.');
      } else {
        console.error('Error signing in:', error);
        throw error;
      }
    }
  }

  static async signOut() {
    Auth.signOut({global: true});
  }

  static async signUp(email_address: string, password: string, attributes: { [key: string]: any }): Promise<any> {
    try {
      const { user, userSub } = await Auth.signUp({
        username: email_address,
        password,
        attributes
      });
      const user_guid = userSub;
      return user_guid;
    } catch (error) {
      if ((error as AWSError).code === 'UsernameExistsException') {
        console.log('Username already exists', error);
        throw new UsernameExistsException('This username already exists.');
      } else {
        console.error('Error when signing up: ', error);
        throw error;
      }
    }
  }

  static async submitVerificationCode(email_address: string, code: string) {
    try {
      await Auth.confirmSignUp(email_address, code);
    } catch (error) {
      console.error('Error when confirming code: ', error);
      throw error;
    }
  }

  static async resendConfirmationCode(email_address: string) {
    try {
      await Auth.resendSignUp(email_address);
    } catch (error) {
      console.error('Error when resending code: ', error);
      throw error;
    }
  }

  static async forgotPassword(email_address: string) {
    try {
      await Auth.forgotPassword(email_address);
    } catch (error) {
      if ((error as AWSError).code === 'UserNotFoundException') {
        console.error('Error when sending password reset code: ', error);
        if(isEmail(email_address)){
          throw new Error("Email doesn't exist in the database.");
        } else {
          throw new Error('This request is invalid. Not a valid email.');
        }
      } else {
        console.error('Error when sending password reset code: ', error);
        throw error;
      }
    }
  }

  static async forgotPasswordSubmit(email_address: string, code: string, newPassword: string) {
    try {
      await Auth.forgotPasswordSubmit(email_address, code, newPassword);
    } catch (error) {
      console.error('Error when resetting password: ', error);
      throw error;
    }
  }

  static async changePassword(oldPassword: string, newPassword: string) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Auth.changePassword(user, oldPassword, newPassword);

      if (result === 'SUCCESS') {
        return result;
      }
    } catch (error) {
      console.error('Error changing password: ', error);
      throw error;
    }
  }

}